import { sortBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";

import * as api from "../../../api";
import useCompetition, {
  useCompetitionJudges,
  useCompetitionRoutes,
} from "../../../hooks/useCompetition";
import { useVenueMaps } from "../../../hooks/useVenueRoutes";
import { useJudgeAuthWall } from "../../../modules/onboarding/components/hooks/useAuthWall";
import { ApiCompetition, ApiJudgingGroup } from "../../../types";

export default function useAssignAreasScreen() {
  const { competitionUUID } = useParams();
  useJudgeAuthWall(`/${competitionUUID}/manage-routes/areas`);
  const { competition, ...competitionReq } = useCompetition(competitionUUID);

  const { judges } = useCompetitionJudges(competitionUUID);
  const venueUUID = competition?.venues?.[0]?.uuid;

  // eslint-disable-next-line prefer-const
  let { routes, ...routesReq } = useCompetitionRoutes({
    venueUUID,
    competitionUUID: competition?.uuid,
  });
  routes = sortBy(routes, (x) => x.circuitNumber);
  const [selectedRouteUUID, _setSelectedRouteUUID] = useState<string | null>();
  const [selectedJudgingGroupUUID, setSelectedJudgingGroupUUID] = useState<
    string | null
  >(null);

  const selectedJudgingGroup = useMemo(
    () =>
      competition?.judgingGroups?.find(
        (x) => x.uuid === selectedJudgingGroupUUID
      ),
    [competition?.judgingGroups, selectedJudgingGroupUUID]
  );

  const filteredRoutes = useMemo(
    () =>
      selectedJudgingGroup === undefined
        ? routes
        : routes?.filter((x) => selectedJudgingGroup.routes.includes(x.uuid)),
    [routes, selectedJudgingGroup]
  );

  const { maps } = useVenueMaps(competition?.venues?.[0]?.uuid);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const handleSelectRouteJudgingGroup = useCallback(
    ({
      routeUUID,
      judgingGroupUUID,
    }: {
      routeUUID: string;
      judgingGroupUUID: string;
    }) => {
      mutate(
        `/competitions/${competitionUUID}`,
        async (competitionToUpdate?: ApiCompetition) => {
          if (!competitionToUpdate) {
            return undefined;
          }
          const updatedJudgingGroups = competitionToUpdate.judgingGroups.map(
            (jg) => {
              if (jg.uuid === judgingGroupUUID) {
                return {
                  ...jg,
                  routes: [...jg.routes, routeUUID],
                  // Need to send judge uuids instead of objects to the API
                  judges: jg.judges.map((x) => x.uuid),
                };
              } else {
                return {
                  ...jg,
                  routes: jg.routes.filter((x) => x !== routeUUID),
                  // Need to send judge uuids instead of objects to the API
                  judges: jg.judges.map((x) => x.uuid),
                };
              }
            }
          );
          const result = await api.updateJudgingGroups(
            competitionToUpdate.uuid,
            updatedJudgingGroups
          );
          return result;
        },
        false
      );
      return {
        routeUUID,
        judgingGroupUUID,
      };
    },
    [competitionUUID]
  );

  const setSelectedRouteUUID = (uuid: string | null) => {
    _setSelectedRouteUUID(uuid);
  };

  const onAddJudgingGroup = async ({ name }: { name: string }) => {
    if (!competitionUUID) {
      return;
    }
    setShowAddModal(false);
    const newJudgingGroup = await api.createJudgingGroup(competitionUUID, {
      name,
    });
    mutate<ApiCompetition>(
      `/competitions/${competitionUUID}`,
      (competitionToUpdate?: ApiCompetition) => {
        if (!competitionToUpdate) {
          return undefined;
        }
        return {
          ...competitionToUpdate,
          judgingGroups: [
            ...competitionToUpdate.judgingGroups,
            newJudgingGroup,
          ],
        };
      }
    );
  };

  const onEditJudgingGroupSubmit = async (
    judgingGroupUUID: string,
    { name }: { name: string }
  ) => {
    if (!competitionUUID) {
      return;
    }
    setShowEditModal(false);
    await api.updateJudgingGroup(competitionUUID, judgingGroupUUID, {
      name,
    });
    mutate(`/competitions/${competitionUUID}`);
  };

  const onDeleteJudgingGroup = async (judgingGroupUUID: string) => {
    if (!competitionUUID) {
      return;
    }
    await api.deleteJudgingGroup(competitionUUID, judgingGroupUUID);
    setShowEditModal(false);
    mutate(`/competitions/${competitionUUID}`);
  };

  const updateJudgingGroupJudges = (
    judgingGroup: ApiJudgingGroup,
    uuids: string[]
  ) => {
    if (competitionUUID) {
      api.updateJudgingGroup(competitionUUID, judgingGroup.uuid, {
        judges: uuids,
      });
      mutate(`/competitions/${competitionUUID}`);
    }
  };

  const [showJudgesBottomSheet, setShowJudgesBottomSheet] = useState(false);

  return {
    routes: filteredRoutes,
    maps,
    isLoading:
      !routes && (routesReq.isValidating || competitionReq.isValidating),
    selectedJudgingGroup,
    selectedJudgingGroupUUID,
    setSelectedJudgingGroupUUID,
    selectedRouteUUID,
    setSelectedRouteUUID,
    showAddModal,
    setShowAddModal,
    showEditModal,
    setShowEditModal,
    competition,
    handleJudgingGroupSelect: handleSelectRouteJudgingGroup,
    showJudgesBottomSheet,
    setShowJudgesBottomSheet,
    onAddJudgingGroup: onAddJudgingGroup,
    onEditJudgingGroup: onEditJudgingGroupSubmit,
    onDeleteJudgingGroup,
    updateJudgingGroupJudges,
    judges,
  };
}
